"use client";

import React from "react";
import { ArrowUp, Facebook, Instagram, Mail, MapPin, Phone } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
function FooterMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <ul className="space-y-3">
        {[1, 2, 3, 4].map((_, i) => <li key={i} data-index={i} className="h-4 w-24 bg-muted/20 animate-pulse rounded" />)}
      </ul>;
  }
  if (!menuItems || menuItems.length === 0) {
    return <ul className="space-y-3 text-sm">
        <li data-index={0}>
          <Link to="#" className="text-muted-foreground hover:text-primary transition-colors font-default">Home</Link>
        </li>
        <li data-index={1}>
          <Link to="#" className="text-muted-foreground hover:text-primary transition-colors font-default">The Doula</Link>
        </li>
        <li data-index={2}>
          <Link to="#" className="text-muted-foreground hover:text-primary transition-colors font-default">Specialized Care</Link>
        </li>
        <li data-index={3}>
          <Link to="#" className="text-muted-foreground hover:text-primary transition-colors font-default">Let’s Connect</Link>
        </li>
      </ul>;
  }
  return <ul className="space-y-3 text-sm">
      {menuItems.map((item, i) => <li key={item.id} data-index={i}>
          <Link to={item.href || "#"} className="text-muted-foreground hover:text-primary transition-colors font-default">
            {item.label}
          </Link>
        </li>)}
    </ul>;
}
export default function Footer() {
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth"
    });
  };
  const currentYear = new Date().getFullYear();
  return <section data-section-id="226030" id="footer" className="bg-background text-foreground pt-16 pb-8">
      <div className="container mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-2 lg:grid-cols-4 lg:gap-8 mb-12">
          {}
          <div className="flex flex-col items-start space-y-6">
            <Link to="/" className="inline-block">
              <img src="https://sunny-rooster.10web.cloud/wp-content/uploads/2026/05/Birthing-Joy-Logo.png" alt="Birthing Joy" className="object-contain !w-[150px] !h-auto rounded-tl-[100px] rounded-tr-[100px] rounded-br-[100px] rounded-bl-[100px]" />
            </Link>
            <p className="text-sm text-muted-foreground leading-relaxed font-default">
              Gentle, expert doula care for birth, postpartum, and lactation. Supporting families in West Palm Beach with warmth and professional guidance.
            </p>
          </div>

          {}
          <div className="flex flex-col space-y-6">
            <h3 className="font-default text-lg font-semibold tracking-wide text-primary">Quick Links</h3>
            <MenuProvider menu_id="17">
              <FooterMenuConsumer />
            </MenuProvider>
          </div>

          {}
          <div className="flex flex-col space-y-6">
            <h3 className="font-default text-lg font-semibold tracking-wide text-primary">Get in Touch</h3>
            <ul className="space-y-4 text-sm text-muted-foreground font-default">
              <li>
                <Link to="tel:5615550123" className="flex items-center gap-3 hover:text-primary transition-colors group">
                  <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-secondary group-hover:bg-accent transition-colors">
                    <Phone className="size-4 text-primary" />
                  </span>
                  <span>(561) 969-8692</span>
                </Link>
              </li>
              <li>
                <Link to="mailto:Birthingjoy.withjoss@gmail.com" className="flex items-center gap-3 hover:text-primary transition-colors group">
                  <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-secondary group-hover:bg-accent transition-colors">
                    <Mail className="size-4 text-primary" />
                  </span>
                  <span>Birthingjoy.withjoss@gmail.com</span>
                </Link>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-secondary mt-0.5">
                  <MapPin className="size-4 text-primary" />
                </span>
                <span className="leading-relaxed">
                  Serving West Palm Beach & Surrounding Florida Areas
                </span>
              </li>
            </ul>
          </div>

          {}
          <div className="flex flex-col space-y-6">
            <h3 className="font-default text-lg font-semibold tracking-wide text-primary">Connect</h3>
            <div className="flex items-center gap-4">
              <Link to="https://www.facebook.com/profile.php?id=100067390804899" className="flex size-10 items-center justify-center rounded-full bg-secondary text-primary hover:bg-primary hover:text-primary-foreground transition-all duration-300 shadow-sm" aria-label="Facebook">
                <Facebook className="size-5" />
              </Link>
              <Link to="https://www.instagram.com/birthingjoy_withjoss/" className="flex size-10 items-center justify-center rounded-full bg-secondary text-primary hover:bg-primary hover:text-primary-foreground transition-all duration-300 shadow-sm" aria-label="Instagram">
                <Instagram className="size-5" />
              </Link>
            </div>
            <div className="pt-2">
              <span className="inline-block px-4 py-2 rounded-full bg-accent text-[10px] font-semibold uppercase tracking-widest text-accent-foreground border border-border/50">
                Lactation certified
              </span>
            </div>
          </div>
        </div>

        <Separator className="bg-border/30 mb-8" />

        {}
        <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
          <p className="text-sm text-muted-foreground text-center sm:text-left font-default">
            &copy; {currentYear} Birthing Joy. All rights reserved.
          </p>
          <Button variant="outline" size="icon" onClick={scrollToTop} className="rounded-full border-border bg-card/50 hover:bg-primary hover:text-primary-foreground hover:border-primary transition-all duration-300 shadow-sm" aria-label="Scroll to top">
            <ArrowUp className="size-4" />
          </Button>
        </div>
      </div>
    </section>;
}